<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProspectingListsAPI from 'dashboard/api/prospectingLists';

// Prospecção "Do CRM": contatos da conta por etiqueta ou por etapa do funil.
// A lista é viva — o Prospecting::CrmSyncJob relê a origem a cada 10 min.
// Usado no hub ("Buscar agora") e, em versão compacta, dentro do nó Prospecção.
const props = defineProps({ compact: { type: Boolean, default: false } });
const emit = defineEmits(['created', 'cancel']);

const { t } = useI18n();
const store = useStore();

const KINDS = ['label', 'stage'];
const kind = ref('label');
const label = ref('');
const funnelId = ref('');
const stageName = ref('');
const name = ref('');
const preview = ref(null);
const isCounting = ref(false);
const isCreating = ref(false);
const error = ref('');

const labels = computed(() => store.getters['labels/getLabels'] || []);
const funnels = computed(() => store.getters['funnels/getFunnels'] || []);

// Mesmo formato do canvas: funnel.stages é um objeto nome -> { label, position }.
const stages = computed(() => {
  const funnel = funnels.value.find(
    f => Number(f.id) === Number(funnelId.value)
  );
  if (!funnel?.stages) return [];
  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1]?.position || 0) - (b[1]?.position || 0))
    .map(([key, cfg]) => ({ key, label: cfg?.label || key }));
});

const crmPayload = computed(() => {
  if (kind.value === 'label') {
    return label.value ? { kind: 'label', label: label.value } : null;
  }
  return funnelId.value && stageName.value
    ? {
        kind: 'stage',
        funnel_id: Number(funnelId.value),
        stage_name: stageName.value,
      }
    : null;
});

const selectClass =
  'w-full rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-3 pr-9 text-sm text-n-slate-12 [background-origin:border-box] bg-[right_0.75rem_center]';
const inputClass =
  'w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10';

const apiError = (err, fallbackKey) =>
  err?.response?.data?.error || t(fallbackKey);

// Contagem ao vivo de quem entra hoje, com um respiro entre trocas de filtro.
let countTimer = null;
async function countNow() {
  const payload = crmPayload.value;
  if (!payload) return;
  isCounting.value = true;
  error.value = '';
  try {
    const { data } = await ProspectingListsAPI.crmPreview({ crm: payload });
    // A resposta de um filtro antigo não pode sobrescrever o atual.
    if (crmPayload.value === payload) preview.value = data;
  } catch (err) {
    preview.value = null;
    error.value = apiError(err, 'LEAD_PROSPECTOR.CRM.PREVIEW_ERROR');
  } finally {
    isCounting.value = false;
  }
}

watch(crmPayload, () => {
  preview.value = null;
  clearTimeout(countTimer);
  countTimer = setTimeout(countNow, 300);
});

const onKindChange = next => {
  kind.value = next;
  error.value = '';
};

// Trocar o funil zera a etapa: etapa de outro funil é pior que etapa vazia.
const onFunnelChange = e => {
  funnelId.value = e.target.value;
  stageName.value = '';
};

async function create() {
  if (isCreating.value || !crmPayload.value) return;
  isCreating.value = true;
  error.value = '';
  try {
    const { data } = await ProspectingListsAPI.create({
      mode: 'crm',
      name: name.value.trim(),
      crm: crmPayload.value,
    });
    useAlert(t('LEAD_PROSPECTOR.CRM.CREATED'));
    emit('created', data);
  } catch (err) {
    error.value = apiError(err, 'LEAD_PROSPECTOR.CRM.CREATE_ERROR');
  } finally {
    isCreating.value = false;
  }
}

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('funnels/get');
});
onBeforeUnmount(() => clearTimeout(countTimer));
</script>

<template>
  <div
    class="flex flex-col"
    :class="props.compact ? 'gap-3' : 'gap-5 px-6 py-5'"
  >
    <div
      class="flex items-start gap-2 rounded-lg bg-n-alpha-2 text-n-slate-11"
      :class="props.compact ? 'p-2 text-xs' : 'p-3 text-sm'"
    >
      <span class="i-lucide-refresh-cw shrink-0 mt-0.5 text-n-brand" />
      <span>{{ t('LEAD_PROSPECTOR.CRM.HINT') }}</span>
    </div>

    <div class="flex gap-2" role="radiogroup">
      <button
        v-for="k in KINDS"
        :key="k"
        type="button"
        role="radio"
        :aria-checked="kind === k"
        :data-testid="`crm-kind-${k}`"
        class="flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm"
        :class="
          kind === k
            ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
            : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="onKindChange(k)"
      >
        <span :class="k === 'label' ? 'i-lucide-tag' : 'i-lucide-kanban'" />
        {{ t(`LEAD_PROSPECTOR.CRM.KINDS.${k.toUpperCase()}`) }}
      </button>
    </div>

    <template v-if="kind === 'label'">
      <p v-if="!labels.length" class="text-sm text-n-slate-10">
        {{ t('LEAD_PROSPECTOR.CRM.NO_LABELS') }}
      </p>
      <label v-else class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('LEAD_PROSPECTOR.CRM.LABEL') }}
        <select v-model="label" data-testid="crm-label" :class="selectClass">
          <option value="">{{ t('LEAD_PROSPECTOR.CRM.LABEL_PICK') }}</option>
          <option v-for="l in labels" :key="l.id" :value="l.title">
            {{ l.title }}
          </option>
        </select>
      </label>
    </template>

    <template v-else>
      <p v-if="!funnels.length" class="text-sm text-n-slate-10">
        {{ t('LEAD_PROSPECTOR.CRM.NO_FUNNELS') }}
      </p>
      <div
        v-else
        class="grid gap-3"
        :class="props.compact ? 'grid-cols-1' : 'md:grid-cols-2'"
      >
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('LEAD_PROSPECTOR.CRM.FUNNEL') }}
          <select
            :value="funnelId"
            data-testid="crm-funnel"
            :class="selectClass"
            @change="onFunnelChange"
          >
            <option value="">{{ t('LEAD_PROSPECTOR.CRM.FUNNEL_PICK') }}</option>
            <option v-for="f in funnels" :key="f.id" :value="String(f.id)">
              {{ f.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('LEAD_PROSPECTOR.CRM.STAGE') }}
          <select
            v-model="stageName"
            data-testid="crm-stage"
            :class="selectClass"
            :disabled="!funnelId"
          >
            <option value="">{{ t('LEAD_PROSPECTOR.CRM.STAGE_PICK') }}</option>
            <option v-for="s in stages" :key="s.key" :value="s.key">
              {{ s.label }}
            </option>
          </select>
        </label>
      </div>
    </template>

    <label class="flex flex-col gap-1 text-xs text-n-slate-11">
      {{ t('LEAD_PROSPECTOR.CRM.NAME') }}
      <input
        v-model="name"
        data-testid="crm-name"
        type="text"
        :placeholder="preview?.default_name || t('LEAD_PROSPECTOR.CRM.NAME_PH')"
        :class="inputClass"
      />
    </label>

    <p
      v-if="isCounting || preview"
      data-testid="crm-preview"
      class="flex items-center gap-2 text-sm"
      :class="preview?.count === 0 ? 'text-n-amber-11' : 'text-n-teal-11'"
    >
      <span v-if="isCounting" class="i-lucide-loader-2 animate-spin" />
      {{
        isCounting
          ? t('LEAD_PROSPECTOR.CRM.COUNTING')
          : t('LEAD_PROSPECTOR.CRM.PREVIEW', { count: preview.count })
      }}
    </p>

    <p
      v-if="error"
      data-testid="crm-error"
      class="flex items-start gap-2 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
    >
      <span class="i-lucide-alert-triangle shrink-0 mt-0.5" />
      {{ error }}
    </p>

    <div class="flex items-center gap-2">
      <button
        type="button"
        data-testid="crm-create"
        class="flex h-9 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:bg-n-brand/90 disabled:opacity-50"
        :disabled="isCreating || !crmPayload"
        @click="create"
      >
        <span
          :class="
            isCreating ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-plus'
          "
        />
        {{
          isCreating
            ? t('LEAD_PROSPECTOR.CRM.CREATING')
            : t('LEAD_PROSPECTOR.CRM.CREATE')
        }}
      </button>
      <button
        v-if="props.compact"
        type="button"
        class="h-9 rounded-lg px-3 text-sm text-n-slate-11 hover:bg-n-alpha-2"
        @click="emit('cancel')"
      >
        {{ t('LEAD_PROSPECTOR.CRM.CANCEL') }}
      </button>
    </div>
  </div>
</template>
